import {
  checkCardCampBatchStTimeExists,
  createCardCampBatch,
  pageCardCampBatch,
  removeCardCampBatch,
  updateCardCampBatch,
} from '@/services/CardCampRequest';
import { BatchState, TCardCampBatch } from '@/types/camp/CardCamp';
import { useRequest } from '@@/plugin-request/request';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  PlusSquareOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  FormInstance,
  Image,
  Input,
  InputRef,
  message,
  Space,
  Switch,
  Table,
  TableColumnsType,
  TableColumnType,
  Typography,
} from 'antd';
import { Modal } from '@digit-fe/digit-component';
import cx from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.less';
import { history, useParams } from 'umi';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterDropdownProps, type FilterValue } from 'antd/es/table/interface';
import CampSettingsEditor from '@/pages/backend/components/CampSettingsEditor';
import { ESwitch } from '@/types/interface';
import { copyText, editParseJson } from '@/utils';
import ImageModuleUpload from '@/pages/backend/cardCampSettings/recruitPageSettings/ImageModuleUpload';
import type { ItemType } from 'antd/es/menu/interface';

const BatchSettingForm = ({
  cardCampId,
  form,
  data,
  isCopyBatch,
}: {
  cardCampId: string;
  form: FormInstance;
  data?: TCardCampBatch;
  isCopyBatch?: boolean;
}) => {
  useEffect(() => {
    const timeHandle = (time: any) => {
      if (isCopyBatch) {
        return null;
      }
      if (time) {
        return dayjs(time);
      }
      return null;
    };
    if (data) {
      form.setFieldsValue({
        ...data,
        stTime: timeHandle(data.stTime),
        enTime: timeHandle(data.enTime),
        rewardTime: timeHandle(data.rewardTime),
        notice: data.notice ? editParseJson(data?.notice) : '',
      });
    } else {
      form.setFieldsValue({
        earlyStudy: ESwitch.OPEN,
      });
    }
  }, [data, isCopyBatch]);

  const notice = useMemo(() => {
    if (data?.notice && typeof data?.notice === 'string') {
      try {
        return JSON.parse(data?.notice);
      } catch (e) {
        return '';
      }
    }
    return '';
  }, [data?.notice]);

  const { run: checkCardCampBatchStTimeExistsRun } = useRequest(
    (cardCampId: string, stTime: Date, batchId?: string) => {
      return checkCardCampBatchStTimeExists(cardCampId, stTime, batchId);
    },
    {
      manual: true,
    },
  );
  const now = dayjs();

  return (
    <Form
      className={cx(styles.FormWrapper)}
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 16 }}
      form={form}
      clearOnDestroy
    >
      <Form.Item
        name="name"
        label={'批次名称'}
        required
        rules={[{ required: true, message: '请输入批次名称' }]}
      >
        <Input showCount maxLength={50} />
      </Form.Item>
      <Form.Item
        name="stTime"
        label={'打卡开始时间'}
        required
        rules={[
          { required: true, message: '请输入打卡开始时间' },
          ({ getFieldValue }) => ({
            async validator(_, value) {
              if (!value && !getFieldValue('stTime')) {
                return Promise.resolve();
              }
              // 校验是否与已有批次的开始时间相同
              const res = await checkCardCampBatchStTimeExistsRun(cardCampId, value, data?.id);
              if (res) {
                return Promise.reject(new Error('不可与已有批次的开始时间相同'));
              }
            },
          }),
        ]}
      >
        <DatePicker
          showTime
          format={'YYYY-MM-DD HH:mm'}
          disabled={isCopyBatch ? false : data && now.isAfter(data.stTime)}
        />
      </Form.Item>
      <Form.Item label={'允许提前学习'}>
        <Form.Item name="earlyStudy" noStyle>
          <Switch />
        </Form.Item>
        <Typography.Text className={cx('text-c0c0c0', 'font-normal', 'text-sm', 'ml-[5px]')}>
          开启后学员报名后即可开始学习，否则需要到打卡开始时间统一开始学习
        </Typography.Text>
      </Form.Item>
      <Form.Item label={'打卡结束时间'} required>
        <Form.Item
          name="enTime"
          noStyle
          rules={[
            { required: true, message: '请输入打卡结束时间' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || !getFieldValue('stTime')) {
                  return Promise.resolve();
                }
                if (dayjs(value).isBefore(dayjs(getFieldValue('stTime')))) {
                  return Promise.reject(new Error('不可早于打卡开始时间'));
                } else {
                  return Promise.resolve();
                }
              },
            }),
          ]}
        >
          <DatePicker
            showTime
            format={'YYYY-MM-DD HH:mm'}
            disabled={isCopyBatch ? false : data && now.isAfter(data.enTime)}
          />
        </Form.Item>
        <Typography.Text className={cx('text-c0c0c0', 'font-normal', 'text-sm')}>
          在此之前未完成打卡成功条件的学员视为打卡失败
        </Typography.Text>
      </Form.Item>
      <Form.Item label={'成绩公布时间'} required>
        <Form.Item
          name="rewardTime"
          noStyle
          rules={[
            { required: true, message: '请输入成绩公布时间' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || !getFieldValue('enTime')) {
                  return Promise.resolve();
                }
                if (dayjs(value).isBefore(dayjs(getFieldValue('enTime')))) {
                  return Promise.reject(new Error('不可早于打卡结束时间'));
                } else {
                  return Promise.resolve();
                }
              },
            }),
          ]}
        >
          <DatePicker
            showTime
            format={'YYYY-MM-DD HH:mm'}
            disabled={isCopyBatch ? false : data && now.isAfter(data.rewardTime)}
          />
        </Form.Item>
        <Typography.Text className={cx('text-c0c0c0', 'font-normal', 'text-sm')}>
          此时间后开始发放有排名条件的奖励，并且判断打卡成功与否，请在此时间之前完成阅卷和精选心得的奖励
        </Typography.Text>
      </Form.Item>
      <Form.Item label={'批次管理员'}>
        <Form.Item name="administrators" noStyle>
          <Input placeholder={'请输入…'} />
        </Form.Item>
        <Typography.Text className={cx('text-c0c0c0', 'font-normal', 'text-sm')}>
          可以设置除了打卡营管理员之外的其他需要查看和管理本批次学员信息的人员，请输入uid，多个uid之间用英文逗号分隔
        </Typography.Text>
      </Form.Item>
      <Form.Item name="qrCode" label={'社群二维码'} className={styles.QrCodeItemWrapper}>
        <ImageModuleUpload
          uploadButton={<PlusOutlined />}
          imageCrop={{ width: 160, height: 160 }}
          maxSize={0.5}
          help={
            <p className={cx('text-c0c0c0', 'font-normal', 'text-sm', 'ml-[10px]')}>
              支持上传文件大小不超过 500 KB 的 .png\.jpg 格式图片，尺寸为160 * 160px
            </p>
          }
        />
      </Form.Item>
      <Form.Item label={'加群按钮名称'}>
        <Form.Item noStyle name="buttonName">
          <Input />
        </Form.Item>
        <Typography.Text className={cx('text-c0c0c0', 'font-normal', 'text-sm')}>
          未设置时按钮文字为“专属学习交流群”
        </Typography.Text>
      </Form.Item>
      <Form.Item name="notice" label={'打卡营公告'}>
        <CampSettingsEditor content={notice} style={{ height: '240px' }} />
      </Form.Item>
    </Form>
  );
};

const BatchSettings = () => {
  const { id: campId } = useParams<{ id: string }>();
  const [open, setOpen] = useState(false);
  const [isCopyBatch, setIsCopyBatch] = useState(false);
  const [modalTitle, setModalTitle] = useState<string>('新建批次');
  const [current, setCurrent] = useState<TCardCampBatch>();
  const [form] = Form.useForm();
  const [data, setData] = useState<TCardCampBatch[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>();
  const [refreshPage, setRefreshPage] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm']) => {
    confirm();
    setSearchText(selectedKeys[0]);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (): TableColumnType<TCardCampBatch> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`搜索题目标题`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            搜索
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            重置
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            关闭
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
  });

  const { loading, run: pageCardCampBatchRun } = useRequest(
    () => {
      return pageCardCampBatch({
        current: pagination.current || 1,
        size: pagination.pageSize || 10,
        cardCampId: campId,
        name: searchText,
        states: filters?.['state'] as BatchState[],
      });
    },
    {
      manual: true,
      onSuccess: (data) => {
        console.log(data.records);
        setData(data.records);
        setPagination({
          ...pagination,
          total: data.total,
        });
      },
    },
  );

  useEffect(() => {
    pageCardCampBatchRun();
  }, [refreshPage, searchText, filters]);

  const { run: removeCardCampBatchRun } = useRequest(
    (id: string) => {
      return removeCardCampBatch(id);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('批次已删除');
        setRefreshPage(refreshPage + 1);
      },
      onError: (err) => {
        message.error(err.message);
      },
    },
  );

  const { run: createCardCampBatchRun } = useRequest(
    (form) => {
      return createCardCampBatch(form);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('创建成功');
        setRefreshPage(refreshPage + 1);
      },
      onError: (err) => {
        message.error(err.message);
      },
    },
  );

  const { run: updateCardCampBatchRun } = useRequest(
    (data: TCardCampBatch) => {
      return updateCardCampBatch(data);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('更新成功');
        setRefreshPage(refreshPage + 1);
      },
      onError: (err) => {
        message.error(err.message);
      },
    },
  );

  const columns: TableColumnsType<TCardCampBatch> = [
    {
      title: '序号',
      dataIndex: 'key',
      key: 'key',
      width: 65,
      align: 'center',
      render: (_value: any, _record: any, index: number) =>
        `${(pagination.current! - 1) * pagination.pageSize! + index + 1}`,
    },
    {
      title: '批次名称',
      dataIndex: 'name',
      width: 350,
      key: 'name',
      render: (value: string, record: any) => (
        <Typography.Text ellipsis={{ tooltip: value }} style={{ maxWidth: '350px' }}>
          {value}
        </Typography.Text>
      ),
      ...getColumnSearchProps(),
    },
    {
      title: '二维码',
      width: 80,
      dataIndex: 'qrCode',
      render: (imgSrc) => (
        <Image
          src={imgSrc}
          width={40}
          height={40}
          preview={{
            mask: <EyeOutlined />,
          }}
        />
      ),
    },
    {
      title: '打卡开始时间',
      dataIndex: 'stTime',
      width: 220,
      render: (time) => <>{time && dayjs(time).format('YYYY-MM-DD HH:mm')}</>,
    },
    {
      title: '打卡结束时间',
      dataIndex: 'enTime',
      width: 220,
      render: (time) => <>{time && dayjs(time).format('YYYY-MM-DD HH:mm')}</>,
    },
    {
      title: '状态',
      dataIndex: 'state',
      align: 'center',
      className: 'state-class',
      width: 120,
      filters: [
        {
          text: '未开始',
          value: BatchState.NOT_START,
        },
        {
          text: '进行中',
          value: BatchState.STARTING,
        },
        {
          text: '已结束',
          value: BatchState.END,
        },
      ],
      render: (value: number) => (
        <>
          {value === BatchState.NOT_START && <span className={cx('text-[#FAAD14]')}>未开始</span>}
          {value === BatchState.STARTING && <span className={'text-[#00C82A]'}>进行中</span>}
          {value === BatchState.END && <span className={cx('text-[#C0C0C0]')}>已结束</span>}
        </>
      ),
    },
    {
      title: '操作',
      align: 'center',
      width: 80,
      dataIndex: 'index',
      render: (text, record) => {
        const menuItems: ItemType[] = [
          {
            label: '编辑',
            key: '1',
            icon: <EditOutlined />,
            onClick: () => {
              setModalTitle('编辑批次');
              setCurrent(record);
              setOpen(true);
            },
          },
          {
            label: '学员管理',
            key: '2',
            icon: <TeamOutlined />,
            onClick: () => {
              history.push(`/v2/camp/manage/${record.id}?tab=student`);
            },
          },
          {
            label: '批次报名链接',
            key: '3',
            icon: <TeamOutlined />,
            onClick: () => {
              copyText(`${location.origin}/v2/camp/${record.cardCampId}?class=${record.id}`, () => {
                message.success('批次报名链接已经复制到剪贴板');
              });
            },
          },
          {
            label: '批次学习链接',
            key: '4',
            icon: <TeamOutlined />,
            onClick: () => {
              copyText(`${location.origin}/v2/camp/class/${record.id}`, () => {
                message.success('批次学习链接已经复制到剪贴板');
              });
            },
          },
          {
            label: '复制批次',
            key: '5',
            icon: <CopyOutlined />,
            onClick: () => {
              setCurrent(record);
              setModalTitle('复制批次');
              setOpen(true);
              setIsCopyBatch(true);
            },
          },
          {
            type: 'divider',
          },
          {
            label: '删除',
            key: '6',
            icon: <DeleteOutlined />,
            onClick: () => {
              Modal.confirm({
                title: '确认删除该批次？',
                content: '批次删除后已报名学员将无法查看此批次，此动作不可撤销，请谨慎操作',
                okButtonProps: { type: 'primary', danger: true },
                okText: '删除',
                cancelText: '取消',
                style: { maxWidth: '416px' },
                icon: <QuestionCircleOutlined />,
                onOk: () => {
                  removeCardCampBatchRun(record.id);
                },
              });
            },
          },
        ];
        if (record.state !== BatchState.NOT_START) {
          // DP-7448 去掉报名链接
          menuItems.splice(
            menuItems.findIndex((it) => it?.key === '3'),
            1,
          );
        }
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: menuItems,
            }}
          >
            <EllipsisOutlined />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className={'m-[24px]'}>
      <Button
        type={'primary'}
        icon={<PlusSquareOutlined />}
        // className={cx('text-sm', 'font-normal', 'text-[#1890FF]')}
        onClick={() => {
          setCurrent(undefined);
          setOpen(true);
        }}
      >
        新建批次
      </Button>
      <Table
        size={'middle'}
        className={cx(styles.TableWrapper, 'mt-[16px]')}
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={{
          ...pagination,
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
        }}
        onChange={(pagination, filters) => {
          setPagination(pagination);
          setFilters(filters);
          setRefreshPage(refreshPage + 1);
        }}
      />
      <Modal
        className={styles.ModalWrapper}
        width={830}
        open={open}
        destroyOnClose
        title={modalTitle}
        onCancel={() => {
          setCurrent(undefined);
          setOpen(false);
          setIsCopyBatch(false);
        }}
        onOk={() => {
          form.validateFields().then((value) => {
            switch (modalTitle) {
              case '复制批次':
              case '新建批次':
                {
                  createCardCampBatchRun({
                    ...value,
                    cardCampId: campId,
                    notice:
                      typeof value.notice === 'string'
                        ? value.notice
                        : JSON.stringify(value.notice),
                    earlyStudy: value.earlyStudy ? ESwitch.OPEN : ESwitch.CLOSE,
                  });
                }
                break;
              case '编辑批次':
                {
                  if (current) {
                    updateCardCampBatchRun({
                      ...value,
                      id: current.id,
                      cardCampId: campId,
                      notice:
                        typeof value.notice === 'string'
                          ? value.notice
                          : JSON.stringify(value.notice),
                      earlyStudy: value.earlyStudy ? ESwitch.OPEN : ESwitch.CLOSE,
                    });
                  }
                }
                break;
            }
            setOpen(false);
            setIsCopyBatch(false);
            setRefreshPage(refreshPage + 1);
          });
        }}
      >
        <BatchSettingForm
          isCopyBatch={isCopyBatch}
          form={form}
          data={current}
          cardCampId={campId}
        />
      </Modal>
    </div>
  );
};

export default BatchSettings;
